import { useState } from "react"
import { Link, router } from "@inertiajs/react"
import { formatDistanceToNow } from "date-fns"
import { Pencil, Trash2 } from "lucide-react"
import { AlertDialog, Box, Button, Card, ContextMenu, Flex, Grid, Heading, Text } from "@radix-ui/themes"
import { z } from "zod"
import { type Project, ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  projects: z.array(ProjectSchema),
})

type Props = z.infer<typeof PropsSchema>

export default function DashboardIndex(rawProps: Props) {
  const props = PropsSchema.parse(rawProps)
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null)

  return (
    <Box width="100%">
      <Flex justify="between" align="start" gap="4" mb="6">
        <Box>
          <Heading size="8" mb="2">
            Projects
          </Heading>
          <Text as="p" size="3" style={{ color: "var(--gray-11)" }}>
            Manage everything from one place.
          </Text>
        </Box>

        <Button variant="classic" asChild>
          <Link href="/projects/new">Create project</Link>
        </Button>
      </Flex>

      <Grid columns={{ initial: "1", sm: "2", lg: "3" }} gap="4">
        {props.projects.map((project) => (
          <ContextMenu.Root key={project.id}>
            <ContextMenu.Trigger>
              <Card variant="classic" size="2" asChild>
                <Link
                  href={`/projects/${project.id}`}
                  style={{ textDecoration: "none", cursor: "pointer" }}
                >
                  <Flex direction="column" gap="4">
                    <Flex align="center" gap="2">
                      <Box
                        flexShrink="0"
                        width="12px"
                        height="12px"
                        style={{
                          backgroundColor: `var(--${project.color}-9)`,
                          borderRadius: "50%",
                        }}
                      />
                      <Heading size="4">{project.name}</Heading>
                    </Flex>

                    <Text as="p" size="2" style={{ color: "var(--gray-11)", minHeight: "40px" }}>
                      {project.description?.trim() || "No description yet."}
                    </Text>

                    <Text size="1" style={{ color: "var(--gray-10)" }}>
                      Created {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
                    </Text>
                  </Flex>
                </Link>
              </Card>
            </ContextMenu.Trigger>
            <ContextMenu.Content>
              <ContextMenu.Item onSelect={() => router.visit(`/projects/${project.id}/edit`)}>
                <Pencil size={14} />
                Edit
              </ContextMenu.Item>
              <ContextMenu.Separator />
              <ContextMenu.Item color="red" onSelect={() => setDeleteTarget(project)}>
                <Trash2 size={14} />
                Delete
              </ContextMenu.Item>
            </ContextMenu.Content>
          </ContextMenu.Root>
        ))}
      </Grid>

      <AlertDialog.Root open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>Delete project</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Are you sure you want to delete &ldquo;{deleteTarget?.name}&rdquo;? This action cannot be undone.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="solid"
                color="red"
                onClick={() => {
                  if (deleteTarget) {
                    router.delete(`/projects/${deleteTarget.id}`)
                  }
                }}
              >
                Delete project
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Box>
  )
}
